import React, { useEffect, useState } from "react"
import { Box, Grid, Typography, Collapse, IconButton, useTheme, Tooltip } from "@mui/material"
import { Container, Row, Col } from "react-bootstrap"
import { DragDropContext } from "react-beautiful-dnd"
import { useHistory } from "react-router-dom"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import PodParticipantsTable from "./components/PodParticipantsTable"
import RenamePodModal from "./components/RenamePodModal"
import RegeneratePodsModal from "./components/RegeneratePodsModal"
import styles from "./league.module.scss"
import { useSelector, useDispatch } from "react-redux"
import {
  moveParticipantToPodAction,
  getLeagueDataAction,
  publishLeagueAction,
  renamePodAction,
  autoGeneratePodsAction
} from "redux/DMV/actions"
import AutorenewIcon from "@mui/icons-material/Autorenew"
import { addParticipantToPodAction } from "redux/DMV/actions/user"
import AddParticipantModal from "./components/AddParticipantModal"

const ViewLeagueContest = () => {
  const theme = useTheme()
  const history = useHistory()
  const dispatch = useDispatch()
  const [breadCrumbs, setBreadcrumbs] = useState([])
  const [expandedPods, setExpandedPods] = useState({})
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const [regenModalOpen, setRegenModalOpen] = useState(false)
  const [addParticipantModalOpen, setAddParticipantModalOpen] = useState(false)
  const [selectedPod, setSelectedPod] = useState(null)
  const { leagueData, leagueId } = useSelector((state) => state.dmv?.league)
  const { isLoading } = useSelector((state) => state.app)

  useEffect(() => {
    document.title = "Contest Details"
    setBreadcrumbs(createBreadcrumbs())
    setExpandedPods({})
  }, [])

  const handleTogglePod = (index) => {
    setExpandedPods((prev) => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const handleRename = (podId) => {
    const pod = leagueData?.pods?.find((p) => p.id === podId)
    if (pod) {
      setSelectedPod(pod)
      setRenameModalOpen(true)
    }
  }

  const handleSaveRename = (newName) => {
    if (selectedPod) {
      dispatch(
        renamePodAction({
          podId: selectedPod.id,
          name: newName,
          onSuccess: () => {
            setRenameModalOpen(false)
            setSelectedPod(null)
            // Refresh league data to show updated name
            dispatch(getLeagueDataAction({ leagueId: leagueId }))
          }
        })
      )
    }
  }

  const handleAddParticipant = (selectedParticipant) => {
    dispatch(
      addParticipantToPodAction({
        payload: {
          league_id: leagueId,
          user_id: selectedParticipant
        },
        podId: selectedPod?.id,
        onSuccess: () => {
          setAddParticipantModalOpen(false)
          setSelectedPod(null)
          dispatch(getLeagueDataAction({ leagueId: leagueId }))
        }
      })
    )
  }
  const handleCloseRenameModal = () => {
    setRenameModalOpen(false)
    setSelectedPod(null)
  }

  const handleOpenRegenerate = () => {
    setRegenModalOpen(true)
  }

  const handleCloseRegenerate = () => {
    setRegenModalOpen(false)
  }

  const handleSaveRegenerate = (numberOfPods) => {
    if (!leagueId) return
    dispatch(
      autoGeneratePodsAction({
        leagueId,
        number_of_pods: numberOfPods,
        onSuccess: () => {
          setRegenModalOpen(false)
          dispatch(getLeagueDataAction({ leagueId }))
        }
      })
    )
  }

  const handlePublish = () => {
    if (window.confirm(`Are you sure you want to publish "${leagueData?.name}"?`)) {
      dispatch(
        publishLeagueAction({
          leagueId: leagueId,
          onSuccess: () => {
            history.push(`/dmv/dashboard/leagues`)
          }
        })
      )
    }
  }

  const onDragEnd = (result) => {
    const { source, destination } = result

    if (!destination) {
      return
    }

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return
    }

    const sourcePodIndex = parseInt(source.droppableId.split("-")[1])
    const destPodIndex = parseInt(destination.droppableId.split("-")[1])

    if (sourcePodIndex === destPodIndex) {
      return
    }

    if (!leagueData?.pods || leagueData.pods.length === 0) {
      return
    }

    const sourcePod = leagueData.pods[sourcePodIndex]
    const destPod = leagueData.pods[destPodIndex]

    const movedParticipant = sourcePod.participants[source.index]

    if (!movedParticipant || !movedParticipant.id || !destPod || !destPod.id) {
      return
    }

    dispatch(
      moveParticipantToPodAction({
        payload: {
          participant_id: movedParticipant.id,
          pod_id: destPod.id,
          league_id: leagueId
        },
        onSuccess: () => {
          dispatch(getLeagueDataAction({ leagueId: leagueId }))
        }
      })
    )
  }

  return (
    <>
      <RegeneratePodsModal
        open={regenModalOpen}
        onClose={handleCloseRegenerate}
        onSave={handleSaveRegenerate}
      />
      <RenamePodModal
        open={renameModalOpen}
        onClose={handleCloseRenameModal}
        podName={selectedPod?.name}
        onSave={handleSaveRename}
      />
      <AddParticipantModal
        open={addParticipantModalOpen}
        onClose={() => setAddParticipantModalOpen(false)}
        onSubmit={handleAddParticipant}
      />
      <Grid container className="p-0">
        <Grid item xs={12}>
          <Header title={leagueData?.name || "Contest Details"} breadCrumbs={breadCrumbs}>
            {leagueData?.status === "draft" && (
              <AppButton
                onClick={handleOpenRegenerate}
                sx={{ padding: "13px 24px", minWidth: "160px", mr: 2 }}>
                <AutorenewIcon sx={{ fontSize: "20px", mr: 1 }} />
                Regenerate Pods
              </AppButton>
            )}
            {leagueData?.status === "draft" && leagueData?.pods?.length > 0 && (
              <AppButton
                onClick={handlePublish}
                sx={{
                  padding: "13px 56px",
                  minWidth: "160px"
                }}>
                Publish
              </AppButton>
            )}
          </Header>
        </Grid>

        <Container style={{ minWidth: "100%" }}>
          <Row style={{ width: "100%" }}>
            <Col lg="12" className="p-0">
              <DragDropContext onDragEnd={onDragEnd}>
                <Box className={styles.podsContainer}>
                  {leagueData?.pods?.length === 0 ? (
                    <Typography
                      variant="h2"
                      sx={{ textAlign: "center", color: theme.palette.text.secondary }}>
                      {isLoading ? "Please wait we are fetching the pods..." : "No pods found"}
                    </Typography>
                  ) : (
                    leagueData?.pods?.map((pod, index) => (
                      <Box key={pod?.id} className={styles.podCard}>
                        <Box
                          className={styles.podHeader}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "24px",
                            padding: "20px 24px"
                          }}>
                          <Tooltip title={pod?.name} arrow placement="top">
                            <Typography
                              variant="h3"
                              sx={{
                                color: theme.palette.text.primary,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "250px",
                                minWidth: "150px"
                              }}>
                              {pod?.name?.length > 20
                                ? `${pod?.name?.substring(0, 20)}...`
                                : pod?.name}
                            </Typography>
                          </Tooltip>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "48px",
                              flex: 1
                            }}>
                            <Typography
                              variant="body1Regular"
                              sx={{ color: theme.palette.text.secondary }}>
                              Number of players:{" "}
                              <span style={{ color: theme.palette.text.primary }}>
                                {pod?.member_count}
                              </span>
                            </Typography>
                            <Typography
                              variant="body1Regular"
                              sx={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}>
                              Boost:{" "}
                              <span style={{ color: theme.palette.secondary.default }}>
                                x{pod?.multiplier}
                              </span>
                            </Typography>
                            <Typography
                              variant="body1Regular"
                              sx={{ color: theme.palette.text.secondary, whiteSpace: "nowrap" }}>
                              Score:{" "}
                              <span style={{ color: theme.palette.text.primary }}>
                                {pod?.total_coins}
                              </span>
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "16px"
                            }}>
                            {leagueData?.status === "running" && (
                              <Typography
                                onClick={(e) => {
                                  setAddParticipantModalOpen(true)
                                  setSelectedPod(pod)
                                  e.stopPropagation()
                                }}
                                variant="body1Regular"
                                color={theme.palette.secondary.default}
                                cursor="pointer">
                                Add Participant
                              </Typography>
                            )}
                            {leagueData?.status !== "running" &&
                              leagueData?.status !== "completed" && (
                                <Typography
                                  onClick={(e) => {
                                    handleRename(pod.id)
                                    e.stopPropagation()
                                  }}
                                  variant="body1Regular"
                                  color={theme.palette.secondary.default}
                                  cursor="pointer">
                                  Rename
                                </Typography>
                              )}

                            <IconButton
                              onClick={() => handleTogglePod(index)}
                              sx={{ color: theme.palette.text.primary }}>
                              {expandedPods[index] ? (
                                <KeyboardArrowUpIcon />
                              ) : (
                                <KeyboardArrowDownIcon />
                              )}
                            </IconButton>
                          </Box>
                        </Box>
                        <Collapse
                          in={expandedPods[index]}
                          timeout="auto"
                          unmountOnExit
                          sx={{ padding: "18px" }}>
                          <Box className={styles.podContent}>
                            <PodParticipantsTable
                              participants={pod.participants}
                              podIndex={index}
                              // isDragDisabled={leagueData?.status === "running"}
                            />
                          </Box>
                        </Collapse>
                      </Box>
                    ))
                  )}
                </Box>
              </DragDropContext>
            </Col>
          </Row>
        </Container>
      </Grid>
    </>
  )
}

export default ViewLeagueContest
